import {
  Link as TSLink,
  Navigate as TSNavigate,
  Outlet as TSOutlet,
  useLocation as useTSLocation,
  useNavigate as useTSNavigate,
  useParams as useTSParams,
  useRouter,
} from "@tanstack/react-router";
import { forwardRef, useCallback, useMemo, type ComponentProps, type ReactNode } from "react";

function parseTo(to: string) {
  const [beforeHash = "", hash] = to.split("#");
  const [pathname = "", searchString] = beforeHash.split("?");
  return {
    pathname: pathname || ".",
    search: searchString ? Object.fromEntries(new URLSearchParams(searchString)) : undefined,
    hash: hash || undefined,
  };
}

type NavigateOptions = { replace?: boolean; state?: unknown };
type NavigateFn = (to: string | number, options?: NavigateOptions) => void;

export function useNavigate(): NavigateFn {
  const navigate = useTSNavigate();
  const router = useRouter();
  return useCallback((to, options) => {
    if (typeof to === "number") return router.history.go(to);
    const target = parseTo(to);
    void navigate({ to: target.pathname, search: target.search as never, hash: target.hash, replace: options?.replace, state: options?.state as never });
  }, [navigate, router]);
}

export function useLocation() {
  const location = useTSLocation();
  return useMemo(() => ({
    pathname: location.pathname,
    search: location.searchStr ? `?${location.searchStr}` : "",
    hash: location.hash ?? "",
    state: location.state ?? null,
    key: `${location.pathname}${location.searchStr ?? ""}`,
  }), [location.hash, location.pathname, location.searchStr, location.state]);
}

export function useParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>() {
  return useTSParams({ strict: false } as never) as T;
}

type LinkProps = Omit<ComponentProps<typeof TSLink>, "to"> & { to: string; replace?: boolean; state?: unknown; children?: ReactNode };
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link({ to, replace, state, children, ...rest }, ref) {
  const target = parseTo(to);
  return <TSLink ref={ref as never} to={target.pathname as never} search={target.search as never} hash={target.hash} replace={replace} state={state as never} {...rest}>{children}</TSLink>;
});

export function Navigate({ to, replace, state }: { to: string; replace?: boolean; state?: unknown }) {
  const target = parseTo(to);
  return <TSNavigate to={target.pathname as never} search={target.search as never} hash={target.hash} replace={replace} state={state as never} />;
}
export const Outlet = TSOutlet;
export const NavLink = Link;
